/* -------------------------------------------------------------------------- */
/* LOGIN MIXIN                                                                */
/* -------------------------------------------------------------------------- */
import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import YRecaptcha from '@components/Recaptcha';
import YFormTitle from '@rogues/Auth/blocks/FormTitle';

export default {
    name: 'LoginMixin',

    components: {
        YRecaptcha,
        YFormTitle,
    },
    
    props: {
        /**
         * Color of the links, buttons and forms
         */
        color: String,

        /**
         * Form Title
         */
        formTitle: String,

        /**
         * Form Description
         */
        formDescription: String,

        /**
         * Form Description Link, shows at the end of the form description
         * Used Types: Modern
         * Sample: {
         * `    title: '',
         * `    route: {
         * `        name: '', params: {}, query: {},
         * `    }
         * `}
         * }
         */
        formDescriptionLink: Object,

        /**
         * Service Name
         */
        service: {
            type   : String,
            default: 'Person',
        },

        /**
         * Service Endpoint Name
         */
        serviceName: {
            type   : String,
            default: 'login',
        },

        /**
         * Forget Route Link
         */
        forgetRouteName: {
            type   : String,
            default: 'auth-forget',
        },

        /**
         * Redirect Route after success login
         */
        redirectRouteName: String,

        /**
         * After Success Login Action
         */
        afterSuccessLoginAction: Function,
    },

    /**
     * @inheritdoc
     */
    data() {
        return {
            keyHelper   : uuid(),
            model       : {},
            configs     : null,
            token       : null,
            showPassword: false,
            recaptcha   : {
                active: false,
            },
            twoFA: {
                need   : null,
                require: null,
            },
        };
    },

    /**
     * @inheritdoc
     */
    created() {
        this.fetchLoginFormConfig();
    },

    computed: {
        /**
         * Forget Route Link
         * 
         * @returns {object}
         */
        forgetRoute() {
            return {
                name  : this.forgetRouteName,
                params: {
                    username: this.model.username,
                },
            };
        },
    },

    watch: {
        configs: {
            /**
             * Set recaptcha config
             */
            handler() {
                this.setRecaptchaType();
            },
        },
    },

    methods: {
        /**
         * Fetch login form config
         *
         * @returns {Promise}
         */
        fetchLoginFormConfig() {
            this.$wait.start('fetch-config');
            return this.$services.Person.loginConfigs().then((response) => {
                this.configs = response.data.results;
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('fetch-config');
            });
        },

        /**
         * Form fields label and placeholder
         * 
         * @param {string} type
         * @param {object} config
         * @returns {object}
         */
        formFieldOptions(type, config = this.configs) {
            const text = {};
            if (config) {
                const acceptValues = config[type].accept;
                if (acceptValues && acceptValues.length) {
                    if (acceptValues.length === 1) {
                        text.label = this.$tc(`fields.${acceptValues[0]}`);
                        text.placeholder = this.$t('fields.placeholder', [this.$tc(`fields.${acceptValues[0]}`)]);
                        if (acceptValues[0] === 'mobile') {
                            text.format = '(DDDD) DDD DDDD';
                        } else if (acceptValues[0] === 'code_melli') {
                            text.format = 'DDD-DDDDDD-D';
                        }
                    } else {
                        const acceptValuesTrans = acceptValues.map((item) => this.$tc(`fields.${item}`));
                        text.label = this.$tc('fields.username');
                        text.placeholder = acceptValuesTrans.join(this.$tc('fields.placeholderJoinCharacter'));
                    }
                }
            }
            return text;
        },

        /**
         * Logout user from vuex
         *
         * @returns {Promise<any>}
         */
        logout() {
            if (localStorage.getItem('token')) {
                this.$services.Person.logout().then(() => {}).catch(() => {});
            }
            return this.$store.dispatch('auth/logout');
        },
        
        /**
         * Login user
         */
        login() {
            if (!this.recaptcha.active || this.recaptcha.size !== 'invisible') {
                this.doLogin();
            } else {
                this.verifyRecaptcha();
            }
        },

        /**
         * Set recaptcha type
         */
        setRecaptchaType() {
            const recaptcha = this.get(this.configs, 'recaptcha');

            if (recaptcha.active) {
                this.recaptcha = {
                    active: true,
                    key   : recaptcha.key,
                    size  : recaptcha.size || 'invisible',
                };
            }
        },
        
        /**
         * Verify recaptcha token
         */
        verifyRecaptcha() {
            this.$refs.recaptcha.verify();
        },
        
        /**
         * Set token and start login
         * (set in recaptcha field)
         *
         * @param {string} token
         */
        beforeLogin(token) {
            this.model['g-recaptcha-response'] = token;
            if (this.recaptcha.size === 'invisible') {
                this.doLogin();
            }
        },
        
        /**
         * Do login
         *
         * @returns {*}
         */
        doLogin() {
            this.$wait.start('login-form');
            return this.$services[this.service][this.serviceName](this.model).then((response) => {
                this.token = response.data.results.access_token;
                this.$http.defaults.headers.common.Authorization = `Bearer ${this.token}`;

                if (response.data.results.need_2fa) {
                    this.twoFA.need = true;
                } else if (response.data.results.requires_2fa) {
                    this.twoFA.require = true;
                } else {
                    this.handleLoginResponse(response);
                }
            }).catch((error) => {
                if (this.recaptcha.active && this.recaptcha.size === 'invisible') {
                    this.$refs.recaptcha.reset();
                }
                this.handleError(error, this.$t('messages.login.error'));
            }).finally(() => {
                this.$wait.end('login-form');
            });
        },
        
        /**
         * Handle successful login response
         *
         * @param {object} response
         */
        handleLoginResponse(response) {
            const accessToken = (this.twoFA.require && this.token) ? this.token : response.data.results.access_token;
            // Set cookie if remember is checked
            if (this.model.remember_me) {
                Cookies.set('token', accessToken, 1);
            }

            this.$store.dispatch('auth/login', accessToken).then(() => {
                this.afterSuccessLogin();
            });
        },

        /**
         * Set Redirect
         */
        setRedirect() {
            if (this.$store.getters['auth/redirect']?.name) {
                this.$router.push(this.$store.getters['auth/redirect']);
                this.$store.dispatch('auth/resetRedirect');
                localStorage.removeItem('redirect');
            }
        },
        
        /**
         * Custom login action
         * Should be overwritten in login page
         */
        afterSuccessLogin() {
            if (this.afterSuccessLoginAction) {
                this.afterSuccessLoginAction();
                this.setRedirect();
            } else if (this.redirectRouteName) {
                this.$router.push({ name: this.redirectRouteName });
            } else if (this.$store.getters['auth/redirect']?.name) {
                this.setRedirect();
            } else {
                this.$router.push({ name: 'home' });
            }
        },

        /**
         * Show and hide the password
         */
        showPasswordChars() {
            this.showPassword = !this.showPassword;
        },
    },
};

